import { Outlet, NavLink, useNavigate, Link } from "react-router-dom";
import { useEffect } from "react";
import { useAuth } from "../lib/auth";
import {
  LayoutDashboard, Calendar, Users, Briefcase, FileText, MessageSquare,
  Star, Image as ImageIcon, BookOpen, Newspaper, HelpCircle, Megaphone,
  Sparkles, LogOut, UserCog, Mail, ExternalLink
} from "lucide-react";
import { LOGO } from "../lib/content";
import { Toaster } from "./ui/sonner";

const LINKS = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/admin/bookings", label: "Bookings", icon: Calendar },
  { to: "/admin/clients", label: "Clients", icon: Users },
  { to: "/admin/projects", label: "Projects", icon: Briefcase },
  { to: "/admin/intake", label: "Intake Forms", icon: FileText },
  { to: "/admin/contact", label: "Contact", icon: MessageSquare },
  { to: "/admin/testimonials", label: "Testimonials", icon: Star },
  { to: "/admin/portfolio", label: "Portfolio", icon: ImageIcon },
  { to: "/admin/blog", label: "Insights", icon: BookOpen },
  { to: "/admin/press", label: "Press", icon: Newspaper },
  { to: "/admin/faqs", label: "FAQs", icon: HelpCircle },
  { to: "/admin/creators", label: "Creators", icon: Sparkles },
  { to: "/admin/newsletter", label: "Newsletter", icon: Mail },
  { to: "/admin/announcements", label: "Announcements", icon: Megaphone },
];

export default function AdminLayout() {
  const { admin, signOut, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !admin) navigate("/admin/login", { replace: true });
  }, [loading, admin, navigate]);

  if (loading || !admin) {
    return <div className="min-h-screen flex items-center justify-center text-sm">Loading…</div>;
  }

  return (
    <div className="min-h-screen admin-shell flex">
      <aside className="hidden lg:flex flex-col w-64 bg-[var(--crest-black)] text-[var(--crest-cream)] sticky top-0 h-screen">
        <Link to="/admin" className="px-6 py-6 flex items-center gap-3 border-b border-white/10" data-testid="admin-logo">
          <img src={LOGO.whiteOnBlack} alt="Upper Crest Image" className="h-12 w-12 object-contain" />
          <div className="text-[10px] tracking-[0.32em] uppercase" style={{ color: "var(--crest-gold)" }}>Admin Console</div>
        </Link>
        <nav className="flex-1 overflow-y-auto py-4 no-scrollbar">
          {LINKS.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `flex items-center gap-3 px-6 py-2.5 text-sm transition-colors ${
                  isActive
                    ? "bg-white/5 text-[var(--crest-gold)] border-l-2 border-[var(--crest-gold)]"
                    : "text-[var(--crest-cream)]/80 hover:text-[var(--crest-gold)] border-l-2 border-transparent"
                }`
              }
              data-testid={`admin-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <Icon size={16} />
              <span>{label}</span>
            </NavLink>
          ))}
          {admin.role === "superadmin" && (
            <NavLink
              to="/admin/team"
              className={({ isActive }) =>
                `flex items-center gap-3 px-6 py-2.5 text-sm transition-colors ${
                  isActive ? "bg-white/5 text-[var(--crest-gold)] border-l-2 border-[var(--crest-gold)]" : "text-[var(--crest-cream)]/80 hover:text-[var(--crest-gold)] border-l-2 border-transparent"
                }`
              }
              data-testid="admin-nav-team"
            >
              <UserCog size={16} />
              <span>Team / Admins</span>
            </NavLink>
          )}
        </nav>
        <div className="border-t border-white/10 px-6 py-4">
          <Link
            to="/"
            target="_blank"
            rel="noreferrer"
            className="text-xs tracking-widest uppercase flex items-center gap-2 hover:text-[var(--crest-gold)] mb-3 text-[var(--crest-cream)]/70"
            data-testid="admin-back-to-site"
          >
            <ExternalLink size={14} /> Back to Site
          </Link>
          <div className="text-xs text-[var(--crest-cream)]/60">Signed in as</div>
          <div className="text-sm font-medium" data-testid="admin-current-user">{admin.name}</div>
          <div className="text-[11px] text-[var(--crest-cream)]/50 mt-0.5">{admin.email}</div>
          <button
            onClick={() => { signOut(); navigate("/admin/login"); }}
            className="mt-3 text-xs tracking-widest uppercase flex items-center gap-2 hover:text-[var(--crest-gold)]"
            data-testid="admin-logout-button"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        <div className="lg:hidden bg-[var(--crest-black)] text-[var(--crest-cream)] px-4 py-3 flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-2">
            <img src={LOGO.whiteOnBlack} alt="Upper Crest Image" className="h-8 w-8 object-contain" />
            <span className="text-[10px] tracking-[0.3em] uppercase" style={{ color: "var(--crest-gold)" }}>Admin Console</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/" target="_blank" rel="noreferrer" className="text-xs uppercase tracking-widest text-[var(--crest-cream)]/70 hover:text-[var(--crest-gold)] flex items-center gap-1" data-testid="admin-mobile-back-to-site">
              <ExternalLink size={12} /> Site
            </Link>
            <button onClick={() => { signOut(); navigate("/admin/login"); }} className="text-xs uppercase tracking-widest">Sign out</button>
          </div>
        </div>
        <div className="p-4 md:p-8">
          <Outlet />
        </div>
      </div>
      <Toaster position="top-right" richColors closeButton />
    </div>
  );
}
